import { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Loader2,
  Eye,
  CheckSquare,
  Square,
  RefreshCw,
  Palette,
  Globe,
} from 'lucide-react';
import { useStudioStore } from '../../../stores/studioStore';
import { useBulkExportStore } from '../../../stores/bulkExportStore';
import { usePreviewStore } from '../../../stores/previewStore';
import { useMappingStore } from '../../../stores/mappingStore';
import { resolveElementsForBrand } from '../../../lib/brandResolver';
import type { SchoolBrand } from '../../../types';
import type { BulkExportTarget } from '../../../types/export';

export function BrandPicker() {
  const { schools, schoolBrands, schoolsLoading, loadSchools, loadSchoolBrands } = useStudioStore();
  const { selectedTargets, addTarget, removeTarget } = useBulkExportStore();
  const [search, setSearch] = useState('');
  const [previewingBrandId, setPreviewingBrandId] = useState<string | null>(null);

  useEffect(() => {
    if (schools.length === 0) loadSchools();
  }, [schools.length, loadSchools]);

  useEffect(() => {
    if (schoolBrands.length === 0 && schools.length > 0) {
      loadSchoolBrands();
    }
  }, [schoolBrands.length, schools.length, loadSchoolBrands]);

  const filtered = useMemo(() => {
    if (!search.trim()) return schoolBrands;
    const q = search.toLowerCase();
    return schoolBrands.filter(
      (b) =>
        b.school_name.toLowerCase().includes(q) ||
        (b.subdomain && b.subdomain.toLowerCase().includes(q))
    );
  }, [schoolBrands, search]);

  const selectedIds = useMemo(
    () => new Set(selectedTargets.map((t) => t.brandId)),
    [selectedTargets]
  );

  const handleToggle = (brand: SchoolBrand) => {
    if (selectedIds.has(brand.id)) {
      removeTarget(brand.id);
    } else {
      const target: BulkExportTarget = {
        brandId: brand.id,
        schoolId: brand.school_id,
        schoolName: brand.school_name,
        subdomain: brand.subdomain ?? null,
        brand,
      };
      addTarget(target);
    }
  };

  const handlePreview = (brand: SchoolBrand) => {
    setPreviewingBrandId(brand.id);

    const { canvasState } = useStudioStore.getState();
    const { elementMappings } = useMappingStore.getState();

    const resolved = resolveElementsForBrand(
      canvasState.elements,
      elementMappings,
      brand
    );

    const previewStore = usePreviewStore.getState();
    if (previewStore.setResolvedElements) {
      previewStore.setResolvedElements(resolved);
    }

    setTimeout(() => setPreviewingBrandId(null), 1500);
  };

  const handleRefresh = () => {
    loadSchoolBrands();
  };

  const selectAll = () => {
    for (const brand of filtered) {
      if (!selectedIds.has(brand.id)) {
        const target: BulkExportTarget = {
          brandId: brand.id,
          schoolId: brand.school_id,
          schoolName: brand.school_name,
          subdomain: brand.subdomain ?? null,
          brand,
        };
        addTarget(target);
      }
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2 border-b border-white/[0.06]">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-1.5 flex-1 px-2.5 py-1.5 rounded-lg bg-[#12121a] border border-white/[0.06]">
            <Search size={14} className="text-white/30" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search brands..."
              className="flex-1 bg-transparent text-sm text-white placeholder:text-white/20 outline-none"
            />
          </div>
          <button
            onClick={handleRefresh}
            className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/[0.04] transition-colors"
            title="Refresh brands"
          >
            <RefreshCw size={14} />
          </button>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-white/30">
            {filtered.length} brands{selectedTargets.length > 0 && ` / ${selectedTargets.length} selected`}
          </span>
          <button
            onClick={selectAll}
            className="text-cyan-400/60 hover:text-cyan-400 transition-colors"
          >
            Select all
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto sidebar-scroll">
        {schoolsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-white/20" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Palette size={24} className="mx-auto mb-2 text-white/10" />
            <p className="text-xs text-white/30">
              {search ? 'No brands match your search' : 'No brands available'}
            </p>
          </div>
        ) : (
          <div className="py-1">
            {filtered.map((brand) => {
              const isSelected = selectedIds.has(brand.id);
              const isPreviewing = previewingBrandId === brand.id;

              return (
                <div
                  key={brand.id}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
                    isSelected ? 'bg-cyan-500/[0.06]' : 'hover:bg-white/[0.02]'
                  }`}
                  onClick={() => handleToggle(brand)}
                >
                  {isSelected ? (
                    <CheckSquare size={14} className="text-cyan-400 flex-shrink-0" />
                  ) : (
                    <Square size={14} className="text-white/20 flex-shrink-0" />
                  )}

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <div
                      className="w-3 h-3 rounded-full border border-white/10"
                      style={{ backgroundColor: brand.rgb_primary || '#666' }}
                    />
                    <div
                      className="w-3 h-3 rounded-full border border-white/10"
                      style={{ backgroundColor: brand.rgb_secondary || '#444' }}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white/80 truncate">{brand.school_name}</div>
                    {brand.subdomain && (
                      <div className="flex items-center gap-1 text-[10px] text-white/25">
                        <Globe size={8} />
                        {brand.subdomain}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreview(brand);
                    }}
                    className={`p-1 rounded transition-colors ${
                      isPreviewing
                        ? 'text-cyan-400 bg-cyan-500/10'
                        : 'text-white/15 hover:text-white/40 hover:bg-white/[0.04]'
                    }`}
                    title="Preview with this brand"
                  >
                    <Eye size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
